import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
} from '@mui/material';
import {
  fetchGuardianMe,
  fetchGuardianIncident,
  reportGuardianIncident,
  submitIncidentFeedback,
} from '../api/guardianApi';
import { forwardGeocode, reverseGeocode, searchGeocode } from '../api/geocodeApi';
import { GUARDIAN_PATHS } from '../routes/guardianPaths';
import { parseReportDraft } from '../utils/reportDraft';

const CATEGORIES = ['fire', 'smoke', 'weather', 'flood', 'earthquake', 'other'];

// 2026-09-25 adversarial pass 10: parseFloat read "37,7" as 37 and
// "37.7abc" as 37.7, filing the report at the wrong place. Only a plain
// decimal (optional sign/exponent) is a coordinate; anything else is NaN.
const COORD_RE = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;
export function parseCoord(value) {
  const text = String(value ?? '').trim();
  return COORD_RE.test(text) ? Number(text) : NaN;
}

function validateCoordinates(latitude, longitude) {
  const lat = parseCoord(latitude);
  const lng = parseCoord(longitude);
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    return 'Latitude must be a number between -90 and 90';
  }
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
    return 'Longitude must be a number between -180 and 180';
  }
  return null;
}

// Why a draft request produced nothing, in words for the person (plan Track 5
// B6, 2026-09-30). The form itself is unaffected by any of these.
const DRAFT_FAILURE_MESSAGES = {
  'no-conversation': 'Tell the assistant what you saw in the chat first, then come back to draft your report.',
  busy: 'The assistant is busy with another reply. Try again in a moment.',
  offline: 'The assistant is unavailable right now. You can still fill in the form yourself.',
  'auth-required': 'Sign in to use the assistant.',
  'too-long': 'The request could not be sent. You can still fill in the form yourself.',
  'agent-error': 'The assistant could not draft a report. You can still fill in the form yourself.',
  interrupted: 'The connection to the assistant dropped before it finished. You can still fill in the form yourself.',
  invalid: 'The assistant reply could not be used as a draft. You can still fill in the form yourself.',
  empty: 'The assistant did not find enough in the conversation to draft anything. Fill in the form yourself.',
};
const DRAFT_FIELD_LABELS = { title: 'Title', category: 'Category', description: 'Description', location: 'Location' };
const DRAFTED_HELP = 'Drafted by the assistant - review before submitting';

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {(() => Promise<{ok: boolean, text?: string, reason?: string}>)} [props.onRequestDraft] -
 *   "Help me fill this": asks the agent for a draft (useChatApp.requestReportDraft).
 *   Absent -> the affordance is hidden. It never submits; the form's own
 *   "Report incident" button is the only submit path.
 * @param {{ lat: number, lon: number, placeName?: string }|null} [props.clientLocation] -
 *   Location the person shared with the chat; used (device values, not agent
 *   values) to pre-fill the place when the draft names none.
 */
export default function ReportIncidentForm({ authToken, onRequestDraft, clientLocation }) {
  const navigate = useNavigate();
  const [entitlements, setEntitlements] = useState(null);
  const [form, setForm] = useState({
    title: '',
    category: 'fire',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
  });
  const [inputMode, setInputMode] = useState('location');
  const [locationQuery, setLocationQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasPendingLocationResolution, setHasPendingLocationResolution] = useState(false);
  const searchTimeout = useRef(null);
  const locationFieldRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [incidentResult, setIncidentResult] = useState(null);
  const [polling, setPolling] = useState(false);
  // "Help me fill this" (plan Track 5 B6, 2026-09-30). `edited` = fields the
  // person has touched (a draft never overwrites them); `drafted` = fields
  // currently showing an unreviewed draft value.
  const [drafting, setDrafting] = useState(false);
  const [draftNotice, setDraftNotice] = useState(null);
  const [drafted, setDrafted] = useState({});
  const editedRef = useRef(new Set());
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const markEdited = (...fields) => {
    fields.forEach((f) => editedRef.current.add(f));
    setDrafted((prev) => {
      if (!fields.some((f) => prev[f])) return prev;
      const next = { ...prev };
      fields.forEach((f) => { delete next[f]; });
      return next;
    });
  };

  // 2026-09-25 adversarial pass 9: every snapshot write bumps entSeq; a /me
  // read applies only if nothing newer landed while it was in flight.
  const entSeq = useRef(0);
  const applyEntitlements = (next) => { entSeq.current += 1; setEntitlements(next); };
  const loadMe = useCallback(async () => {
    if (!authToken) return;
    const seq = ++entSeq.current;
    const res = await fetchGuardianMe(authToken);
    if (res.success && seq === entSeq.current) {
      setEntitlements(res.data.entitlements);
    }
  }, [authToken]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (inputMode !== 'location') return undefined;
    if (!locationQuery || locationQuery.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return undefined;
    }

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await searchGeocode(authToken, locationQuery);
        setSearchResults(results);
        setShowDropdown(locationQuery.trim().length >= 2);
      } catch {
        setSearchResults([]);
        setShowDropdown(locationQuery.trim().length >= 2);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [locationQuery, inputMode, authToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationFieldRef.current && !locationFieldRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (field) => (e) => {
    markEdited(field);
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLocationQueryChange = (e) => {
    const value = e.target.value;
    markEdited('location', 'latitude', 'longitude');
    setLocationQuery(value);
    setForm((prev) => ({ ...prev, location: value }));
    setShowDropdown(value.trim().length >= 2);
    setHasPendingLocationResolution(value.trim().length > 0);
  };

  const handleLocationSelect = (result) => {
    markEdited('location', 'latitude', 'longitude');
    setForm((prev) => ({
      ...prev,
      location: result.placeName,
      latitude: String(result.lat),
      longitude: String(result.lng),
    }));
    setLocationQuery(result.placeName);
    setSearchResults([]);
    setShowDropdown(false);
    setHasPendingLocationResolution(false);
  };

  const handleApplyCoordinates = async () => {
    const coordError = validateCoordinates(form.latitude, form.longitude);
    if (coordError) {
      setError(coordError);
      return;
    }
    setError(null);
    const lat = parseCoord(form.latitude);
    const lng = parseCoord(form.longitude);
    const place = await reverseGeocode(authToken, lat, lng);
    if (place) {
      setForm((prev) => ({ ...prev, location: place }));
      setLocationQuery(place);
    }
    setHasPendingLocationResolution(false);
  };

  const resolveLocationCoordinates = async () => {
    const typedLocation = (locationQuery || form.location).trim();
    if (!typedLocation) {
      const coordError = validateCoordinates(form.latitude, form.longitude);
      return coordError ? null : {
        latitude: parseCoord(form.latitude),
        longitude: parseCoord(form.longitude),
      };
    }

    if (!hasPendingLocationResolution && form.latitude && form.longitude) {
      const coordError = validateCoordinates(form.latitude, form.longitude);
      if (!coordError) {
        return {
          latitude: parseCoord(form.latitude),
          longitude: parseCoord(form.longitude),
        };
      }
    }

    const match = await forwardGeocode(authToken, typedLocation);
    if (!match) return null;

    setForm((prev) => ({
      ...prev,
      location: match.placeName,
      latitude: String(match.lat),
      longitude: String(match.lng),
    }));
    setLocationQuery(match.placeName);
    setHasPendingLocationResolution(false);
    return { latitude: match.lat, longitude: match.lng };
  };

  /**
   * Asks the agent for a draft and pre-fills the form with it. Fields the
   * person already edited are kept; fields the agent left out stay blank and
   * are named in the notice. Coordinates are never taken from the agent: a
   * drafted place name is geocoded by the normal submit path, and device
   * coordinates come only from the location the person shared.
   */
  const handleHelpFill = async () => {
    if (!onRequestDraft || drafting) return;
    setDrafting(true);
    setDraftNotice(null);
    setError(null);
    let outcome;
    try {
      const res = await onRequestDraft();
      outcome = res?.ok ? parseReportDraft(res.text) : { ok: false, reason: res?.reason || 'agent-error' };
    } catch {
      outcome = { ok: false, reason: 'agent-error' };
    }
    if (!mountedRef.current) return;
    setDrafting(false);
    if (!outcome.ok) {
      setDraftNotice({
        severity: outcome.reason === 'no-conversation' ? 'info' : 'warning',
        message: DRAFT_FAILURE_MESSAGES[outcome.reason] || DRAFT_FAILURE_MESSAGES['agent-error'],
      });
      return;
    }

    const { draft } = outcome;
    const filled = [];
    const patch = {};
    ['title', 'category', 'description'].forEach((f) => {
      if (draft[f] && !editedRef.current.has(f)) {
        patch[f] = draft[f];
        filled.push(f);
      }
    });
    const locationFree = !['location', 'latitude', 'longitude'].some((f) => editedRef.current.has(f));
    if (locationFree && draft.location) {
      patch.location = draft.location;
      patch.latitude = '';
      patch.longitude = '';
      filled.push('location');
      setInputMode('location');
      setLocationQuery(draft.location);
      setHasPendingLocationResolution(true);
      setSearchResults([]);
      setShowDropdown(false);
    } else if (
      locationFree && !form.location && !form.latitude && !form.longitude
      && Number.isFinite(clientLocation?.lat) && Number.isFinite(clientLocation?.lon)
    ) {
      // Device values, not agent values.
      const place = clientLocation.placeName || '';
      patch.location = place;
      patch.latitude = String(clientLocation.lat);
      patch.longitude = String(clientLocation.lon);
      filled.push('location');
      setInputMode(place ? 'location' : 'coordinates');
      setLocationQuery(place);
      setHasPendingLocationResolution(false);
    }
    setForm((prev) => ({ ...prev, ...patch }));
    setDrafted((prev) => {
      const next = { ...prev };
      filled.forEach((f) => { next[f] = true; });
      return next;
    });
    const missing = ['title', 'category', 'description', 'location']
      .filter((f) => !filled.includes(f) && !editedRef.current.has(f));
    setDraftNotice({
      severity: 'info',
      message: `Draft filled in: ${filled.map((f) => DRAFT_FIELD_LABELS[f]).join(', ') || 'nothing new'}.`
        + (missing.length ? ` Not drafted (left for you): ${missing.map((f) => DRAFT_FIELD_LABELS[f]).join(', ')}.` : '')
        + ' Review and edit, then press Report incident. Nothing has been submitted.',
    });
  };

  const buildPayload = (startTrial = false, coords) => ({
    title: form.title,
    category: form.category,
    description: form.description || null,
    location: form.location || null,
    coordinates: [coords.latitude, coords.longitude],
    start_trial: startTrial,
  });

  const submitIncident = async (startTrial = false) => {
    setSubmitting(true);
    setError(null);

    let coords;
    if (inputMode === 'location') {
      const resolved = await resolveLocationCoordinates();
      if (!resolved) {
        setError('Enter a location and select a match, or switch to coordinates mode');
        setSubmitting(false);
        return;
      }
      coords = resolved;
    } else {
      const coordError = validateCoordinates(form.latitude, form.longitude);
      if (coordError) {
        setError(coordError);
        setSubmitting(false);
        return;
      }
      coords = {
        latitude: parseCoord(form.latitude),
        longitude: parseCoord(form.longitude),
      };
    }

    try {
      const { status, body } = await reportGuardianIncident(
        authToken,
        buildPayload(startTrial, coords)
      );
      if (status === 429) {
        setError(body.message || 'Daily report limit reached');
        if (body.data?.entitlements) applyEntitlements(body.data.entitlements);
        return;
      }
      if (!body.success) {
        setError(body.message || 'Failed to report incident');
        return;
      }
      // 2026-09-25 adversarial pass 7: Backend returns 201 with
      // entitlements=null when the post-commit snapshot fails. Keep the
      // previous snapshot; a null overwrite made handleSubmit a silent no-op.
      // Was: setEntitlements(body.data.entitlements);
      // 2026-09-25 adversarial pass 8: the kept snapshot is one report stale
      // (or absent after a failed first load); re-read it from /me.
      if (body.data?.entitlements) applyEntitlements(body.data.entitlements);
      else loadMe();
      setIncidentResult({ id: body.data.incident_id, event: null, polling: true });
      pollIncident(body.data.incident_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pollIncident = async (incidentId) => {
    setPolling(true);
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i += 1) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetchGuardianIncident(authToken, incidentId);
        if (res.success && res.data?.event) {
          const event = res.data.event;
          const insights = event.insights || {};
          const hasInsights = Boolean(insights.summary || insights.risk_level);
          setIncidentResult({ id: incidentId, event, polling: !hasInsights });
          if (hasInsights) {
            setPolling(false);
            return;
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to refresh incident insights');
        setPolling(false);
        return;
      }
    }
    setPolling(false);
    setIncidentResult((prev) => (prev ? { ...prev, polling: false } : prev));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // 2026-09-25 adversarial pass 8: a failed first /me left entitlements
    // null and made every submit a silent no-op until reload. The snapshot is
    // advisory (the Backend enforces caps), so submit regardless. Was:
    // if (!entitlements) return;

    submitIncident(false);
  };

  const handleFeedback = async (rating) => {
    if (!incidentResult?.id) return;
    setFeedbackError(null);
    try {
      const res = await submitIncidentFeedback(authToken, incidentResult.id, rating, null);
      if (!res.success) {
        setFeedbackError(res.message || 'Failed to submit feedback');
        return;
      }
      setFeedbackSent(true);
    } catch (err) {
      setFeedbackError(err.message || 'Failed to submit feedback');
    }
  };

  const renderInsights = () => {
    if (!incidentResult?.event) return null;
    const event = incidentResult.event;
    const insights = event.insights || {};
    const upgradeMessage = event.upgrade?.message || 'Unlock Guardian Pro to view Full Insights';
    const showLockedPreview = event.gated && (insights.insights_locked || insights.summary);

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Incident insights</Typography>
        {event.gated && (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Preview only — choose Citizen, Researcher, or Pro to unlock full insights.
            A 1-week trial is offered when you add a payment method.
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={() => navigate(GUARDIAN_PATHS.subscriptions)}
            >
              Choose a plan
            </Button>
          </Alert>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Summary:</strong>{' '}
          {insights.summary || (incidentResult.polling ? 'Processing…' : '—')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Risk level:</strong> {insights.risk_level || '—'}
        </Typography>
        {showLockedPreview && (
          <Box
            sx={{
              position: 'relative',
              mt: 2,
              minHeight: 120,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                p: 2,
                filter: 'blur(6px)',
                userSelect: 'none',
                pointerEvents: 'none',
                color: 'text.secondary',
              }}
              aria-hidden
            >
              <Typography variant="body2">
                Full tactical analysis, environmental context, spread projections, and
                recommended response priorities would appear here for Guardian Pro members.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Additional threat vectors, weather coupling, fuel models, and evacuation
                considerations are included in the complete insight report.
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(2px)',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {upgradeMessage}
              </Typography>
            </Box>
          </Box>
        )}
        {!event.gated && insights.insights && (
          <Typography variant="body2" component="pre" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(insights.insights, null, 2)}
          </Typography>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => handleFeedback(1)} disabled={feedbackSent}>
            Helpful
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleFeedback(-1)} disabled={feedbackSent}>
            Not helpful
          </Button>
          {feedbackSent && (
            <Typography variant="caption" color="success.main">Thanks for your feedback</Typography>
          )}
        </Box>
        {feedbackError && (
          <Alert severity="error" sx={{ mt: 1 }}>{feedbackError}</Alert>
        )}
      </Box>
    );
  };

  return (
    <Paper className="il-card" elevation={0} sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>Report an incident</Typography>

      {entitlements && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Tier: ${entitlements.tier}`} size="small" />
          <Chip
            label={
              entitlements.dev_unlimited_reports
                ? 'Reports: unlimited (dev)'
                : `Reports left today: ${entitlements.reports_remaining_today}`
            }
            size="small"
            color="primary"
          />
          {entitlements.trial_active && (
            <Chip label="Trial active" size="small" color="success" />
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {onRequestDraft && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHelpFill}
            disabled={drafting || !authToken}
          >
            {drafting ? 'Drafting…' : 'Help me fill this'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            The assistant drafts from your chat; you review and submit.
          </Typography>
          {draftNotice && (
            <Alert severity={draftNotice.severity} sx={{ mt: 1 }} onClose={() => setDraftNotice(null)}>
              {draftNotice.message}
            </Alert>
          )}
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Title"
          value={form.title}
          onChange={handleChange('title')}
          required
          helperText={drafted.title ? DRAFTED_HELP : undefined}
        />
        <TextField
          select
          label="Category"
          value={form.category}
          onChange={handleChange('category')}
          helperText={drafted.category ? DRAFTED_HELP : undefined}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Description"
          multiline
          minRows={3}
          value={form.description}
          onChange={handleChange('description')}
          helperText={drafted.description ? DRAFTED_HELP : undefined}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Location input</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={inputMode}
            onChange={(_e, value) => value && setInputMode(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="location">Search location</ToggleButton>
            <ToggleButton value="coordinates">Enter coordinates</ToggleButton>
          </ToggleButtonGroup>

          {inputMode === 'location' ? (
            <Box ref={locationFieldRef} sx={{ position: 'relative' }}>
              <TextField
                label="Location"
                placeholder={isSearching ? 'Searching…' : 'Type address or place'}
                value={locationQuery}
                onChange={handleLocationQueryChange}
                onFocus={() => {
                  if (locationQuery.trim().length >= 2) setShowDropdown(true);
                }}
                fullWidth
                helperText={drafted.location ? DRAFTED_HELP : undefined}
              />
              {showDropdown && searchResults.length > 0 && (
                <Paper
                  className="il-card"
                  elevation={0}
                  sx={{
                    position: 'absolute',
                    zIndex: 10,
                    width: '100%',
                    mt: 0.5,
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  <List dense disablePadding>
                    {searchResults.map((result) => (
                      <ListItemButton
                        key={`${result.placeName}-${result.lat}-${result.lng}`}
                        onClick={() => handleLocationSelect(result)}
                      >
                        <ListItemText
                          primary={result.placeName.split(', ')[0]}
                          secondary={result.placeName.split(', ').slice(1).join(', ') || undefined}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
              {showDropdown && isSearching && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Searching for locations…
                </Typography>
              )}
              {showDropdown && !isSearching && locationQuery.trim().length >= 2 && searchResults.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  No matching locations found
                </Typography>
              )}
              {form.location && form.latitude && form.longitude && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {form.location} ({form.latitude}, {form.longitude})
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Latitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  placeholder="e.g. 37.7749"
                  value={form.latitude}
                  onChange={handleChange('latitude')}
                  fullWidth
                />
                <TextField
                  label="Longitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  placeholder="e.g. -122.4194"
                  value={form.longitude}
                  onChange={handleChange('longitude')}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleApplyCoordinates} sx={{ mt: 1, whiteSpace: 'nowrap' }}>
                  Apply
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Enter latitude (-90 to 90) and longitude (-180 to 180), then apply to resolve the place name.
              </Typography>
              {form.location && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Resolved: {form.location}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Button type="submit" variant="contained" disabled={submitting || !authToken}>
          {submitting ? 'Submitting…' : 'Report incident'}
        </Button>
      </Box>

      {polling && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Waiting for AI insights…</Typography>
        </Box>
      )}

      {renderInsights()}
    </Paper>
  );
}
